import React from 'react';
import {
  Box, Card, CardContent, Typography, Chip, LinearProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Avatar, Tabs, Tab, alpha,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
} from '@mui/icons-material';

export const COLORS = {
  teal: { 50: '#f0fdfa', 100: '#ccfbf1', 200: '#99f6e4', 300: '#5eead4', 400: '#2dd4bf', 500: '#14b8a6', 600: '#0d9488', 700: '#0f766e', 800: '#115e59', 900: '#134e4a' },
  blue: '#3b82f6', indigo: '#6366f1', purple: '#8b5cf6', pink: '#ec4899',
  orange: '#f97316', amber: '#f59e0b', red: '#ef4444', green: '#22c55e',
  slate: { 50: '#f8fafc', 100: '#f1f5f9', 200: '#e2e8f0', 300: '#cbd5e1', 400: '#94a3b8', 500: '#64748b', 600: '#475569', 700: '#334155', 800: '#1e293b', 900: '#0f172a' },
};

export const CHART_COLORS = ['#0d9488', '#3b82f6', '#8b5cf6', '#f97316', '#ec4899', '#22c55e', '#f59e0b', '#ef4444', '#6366f1', '#14b8a6'];

export function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export function StyledTabs({ value, onChange, tabs }) {
  return (
    <Paper sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', mb: 0 }}>
      <Tabs
        value={value}
        onChange={onChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          '& .MuiTab-root': {
            textTransform: 'none', fontWeight: 500, fontSize: '0.85rem',
            minHeight: 48, color: COLORS.slate[500],
          },
          '& .Mui-selected': { color: `${COLORS.teal[700]} !important`, fontWeight: 600 },
          '& .MuiTabs-indicator': { backgroundColor: COLORS.teal[600], height: 3, borderRadius: '3px 3px 0 0' },
        }}
      >
        {tabs.map((tab, i) => <Tab key={i} label={tab} />)}
      </Tabs>
    </Paper>
  );
}

export function KPICard({ title, value, subtitle, trend, trendValue, icon: Icon, color = COLORS.teal[600] }) {
  const trendColor = trend === 'up' ? COLORS.green : trend === 'down' ? COLORS.red : COLORS.slate[400];
  const TrendIcon = trend === 'up' ? TrendingUpIcon : trend === 'down' ? TrendingDownIcon : TrendingFlatIcon;
  return (
    <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid', borderColor: 'grey.100', height: '100%', transition: 'transform 0.2s, box-shadow 0.2s', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 25px rgba(0,0,0,0.1)' } }}>
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
          <Typography variant="body2" sx={{ color: COLORS.slate[500], fontWeight: 500, fontSize: '0.8rem' }}>{title}</Typography>
          {Icon && (
            <Avatar sx={{ width: 36, height: 36, bgcolor: alpha(color, 0.1), color }}>
              <Icon sx={{ fontSize: '1.2rem' }} />
            </Avatar>
          )}
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 700, color: COLORS.slate[800], mb: 0.5, fontSize: '1.5rem' }}>{value}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {trendValue && (
            <Chip
              icon={<TrendIcon sx={{ fontSize: '0.9rem !important' }} />}
              label={trendValue}
              size="small"
              sx={{ bgcolor: alpha(trendColor, 0.1), color: trendColor, fontWeight: 600, fontSize: '0.7rem', height: 22, '& .MuiChip-icon': { color: trendColor } }}
            />
          )}
          {subtitle && (
            <Typography
              variant="caption"
              sx={{
                color: COLORS.slate[300],
                ml: 0.5,
                fontSize: '0.65rem',
                fontWeight: 400,
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

export function SectionHeader({ title, subtitle }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, color: COLORS.slate[800], mb: 0.5 }}>{title}</Typography>
      {subtitle && <Typography variant="body2" sx={{ color: COLORS.slate[500] }}>{subtitle}</Typography>}
    </Box>
  );
}

export function DataTable({ columns, rows, stickyHeader = true, maxHeight = 500, sx: containerSx }) {
  return (
    <TableContainer
      component={Paper}
      sx={{
        borderRadius: 2,
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        border: '1px solid',
        borderColor: 'grey.100',
        maxHeight,
        ...containerSx,
      }}
    >
      <Table stickyHeader={stickyHeader} size="small">
        <TableHead>
          <TableRow>
            {columns.map((col, i) => (
              <TableCell key={i} align={col.align || 'left'} sx={{ fontWeight: 700, bgcolor: COLORS.slate[50], color: COLORS.slate[700], fontSize: '0.8rem', py: 1.5, borderBottom: `2px solid ${COLORS.teal[200]}`, whiteSpace: 'nowrap' }}>
                {col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, ri) => (
            <TableRow
              key={ri}
              sx={{
                '&:hover': { bgcolor: row.isTotal ? COLORS.slate[100] : COLORS.teal[50] },
                transition: 'background-color 0.15s',
                bgcolor: row.isTotal ? COLORS.slate[50] : 'inherit',
              }}
            >
              {columns.map((col, ci) => (
                <TableCell
                  key={ci}
                  align={col.align || 'left'}
                  sx={{
                    fontSize: '0.82rem',
                    py: 1.2,
                    color: COLORS.slate[700],
                    fontWeight: row.isTotal ? 700 : 400,
                    borderTop: row.isTotal ? `2px solid ${COLORS.teal[200]}` : 'none',
                  }}
                >
                  {col.render ? col.render(row[col.field], row) : row[col.field]}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export function ChartCard({ title, subtitle, children, height = 300 }) {
  return (
    <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid', borderColor: 'grey.100', height: '100%' }}>
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: COLORS.slate[800], fontSize: '0.95rem' }}>{title}</Typography>
          {subtitle && <Typography variant="caption" sx={{ color: COLORS.slate[400] }}>{subtitle}</Typography>}
        </Box>
        <Box sx={{ height, width: '100%' }}>{children}</Box>
      </CardContent>
    </Card>
  );
}

export function ProgressBar({ label, value, max = 100, color = COLORS.teal[500] }) {
  const pct = Math.round((value / max) * 100);
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, color: COLORS.slate[600], fontSize: '0.82rem' }}>{label}</Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.slate[700], fontSize: '0.82rem' }}>{value} / {max} ({pct}%)</Typography>
      </Box>
      <LinearProgress variant="determinate" value={pct} sx={{ height: 8, borderRadius: 4, bgcolor: alpha(color, 0.15), '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 4 } }} />
    </Box>
  );
}

export function StatusChip({ label, color }) {
  return <Chip label={label} size="small" sx={{ bgcolor: alpha(color, 0.1), color, fontWeight: 600, fontSize: '0.72rem', height: 22 }} />;
}

export function RankBadge({ rank }) {
  const badges = { 1: { bg: '#fef3c7', color: '#d97706', label: '🥇' }, 2: { bg: '#e5e7eb', color: '#6b7280', label: '🥈' }, 3: { bg: '#fed7aa', color: '#ea580c', label: '🥉' } };
  const badge = badges[rank];
  if (badge) {
    return <Typography sx={{ fontSize: '1.2rem', textAlign: 'center' }}>{badge.label}</Typography>;
  }
  return <Typography sx={{ fontWeight: 600, color: COLORS.slate[500], textAlign: 'center', fontSize: '0.85rem' }}>#{rank}</Typography>;
}

export function PageContainer({ children }) {
  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh', pt: 1, pb: 3, px: { xs: 1, sm: 2, md: 3 } }}>
      {children}
    </Box>
  );
}
